// paex-store-core.js — shared drive-to-store foundation: constants, helpers,
// Re-execution-safe (var + define guards): more than one script tag for this
// file can legitimately exist on a page (PDP block + chip both emit one).
// and the <paex-store-drawer> element (selector v2). Loaded (deferred, FIRST)
// by every consumer: the Availability - PE PDP block and the My-store chip.
// Hosts embed their own data and expose: data.{stores,texts}, storeId,
// variantId (may be null), tierTextFor()/tierStateFor().

var PAEX_STORE_COOKIE = 'paex_preferred_store';
var PAEX_STORE_COOKIE_HOURS = 8760; // 365 days
var PAEX_STORE_EVENT = 'paex:store-change';
var PAEX_RCC_STORE_COOKIE = 'rcc-place-id';
var PAEX_RCC_STORE_SET_EVENT = 'retail-connect:place:set';
var PAEX_RCC_STORE_REMOVED_EVENT = 'retail-connect:place:removed';
var PAEX_GEO_EVENT = 'paex:geo-ready';
var PAEX_GEO_SESSION_KEY = 'paex:geo';
var PAEX_DEST_EVENT = 'paex:dest-change';
var PAEX_DEST_SESSION_KEY = 'paex:dest';

// Compact display name for tight spaces (block title, chip, suggestion):
// every store shares the "Party Expert - " / "Halloween Expert - " brand
// prefix, so ellipsis truncation eats exactly the distinguishing part.
// The drawer rows keep FULL names. Separator class covers hyphen, en dash
// AND em dash (U+2014 — used in design mockups; live data uses "-").
function paexDeliveryShortName(name) {
  return String(name || '').replace(/^\s*(party|halloween)\s+expert\s*[-–—]\s*/i, '');
}

// Collision-safe display names for the whole directory: short names (city
// only) normally. When two stores' short names collide (live: "Party Expert
// - Québec" + "Halloween Expert - Québec"), the Halloween Expert entry gets
// a short brand tag — "Québec · Halloween" — while Party Expert keeps the
// bare city (merchant ruling 2026-08-03, supersedes the 2026-07-12
// full-names-on-collision rule). Tag word comes from
// paex.store_drawer.brand_tag_halloween via window.paexStoreDrawerText
// (serialized in paex-store-chip.liquid); EN and FR are both "Halloween",
// which the literal fallback mirrors for hosts without the chip embed.
// FLAGGED edge (no such pair live): a SAME-brand collision (two PE or two
// HE stores sharing a city) stays ambiguous after tagging, so those rows
// fall back to full banner names until the merchant picks a disambiguator
// (province/neighborhood).
function paexDeliveryDisplayNames(stores) {
  const counts = {};
  Object.keys(stores).forEach((id) => {
    const short = paexDeliveryShortName(stores[id].name);
    counts[short] = (counts[short] || 0) + 1;
  });
  const tag =
    (typeof window !== 'undefined' &&
      window.paexStoreDrawerText &&
      window.paexStoreDrawerText.halloweenTag) ||
    'Halloween';
  const names = {};
  const displayCounts = {};
  Object.keys(stores).forEach((id) => {
    const full = String(stores[id].name || '');
    const short = paexDeliveryShortName(full);
    const isHalloween = /^\s*halloween\s+expert\s*[-–—]/i.test(full);
    names[id] = counts[short] > 1 && isHalloween ? short + ' · ' + tag : short;
    displayCounts[names[id]] = (displayCounts[names[id]] || 0) + 1;
  });
  Object.keys(stores).forEach((id) => {
    if (displayCounts[names[id]] > 1) names[id] = String(stores[id].name || '');
  });
  return names;
}

// ---- Store type badge + open/close DATE rendering (2026-08-03) ----
// Blob fields: type ("permanent" | "seasonal_popup"), opening, closing
// (ISO "YYYY-MM-DD"). Rendered in the drawer rows only (namerow pill +
// dates line). DESIGN-SOURCE FLAG: the complete Jul-31 design export
// contains NO frame for any of these elements — copy/placement built from
// the merchant spec (2026-08-03), styled with the row's established
// classes (__open green, __soon amber) and scheme vars; the chip flyout
// carries no mirror (no frame shows one).

// TIMEZONE-SAFE ISO parse: split Y/M/D into a LOCAL-midnight Date. Never
// new Date("YYYY-MM-DD") — that parses as UTC midnight and shifts the date
// one day back in every timezone west of UTC (all of Québec).
function paexStoreParseIsoDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').trim());
  if (!m) return null;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

// Absolute date with weekday per the fresh design frames ("Opens Sat,
// Oct 24" / "Closes Wed, Nov 4"; FR "sam. 24 oct."). Locale defaults to
// the document language (Shopify sets <html lang>).
function paexStoreFormatDate(iso, locale) {
  const date = paexStoreParseIsoDate(iso);
  if (!date) return '';
  const lang =
    locale || (typeof document !== 'undefined' && document.documentElement.lang) || 'en';
  const options = { weekday: 'short', day: 'numeric', month: 'short' };
  try {
    return new Intl.DateTimeFormat(lang, options).format(date);
  } catch (error) {
    return new Intl.DateTimeFormat('en', options).format(date);
  }
}

// Design rule (3a): opening/closing evaluate in the WAREHOUSE timezone,
// never the browser's — same rule as the dispatch cutoff. A popup must not
// close early for a traveller.
var PAEX_STORE_DATES_TZ = 'America/Toronto';
function paexStoreDayIso(instant) {
  try {
    // en-CA yields YYYY-MM-DD
    return new Intl.DateTimeFormat('en-CA', { timeZone: PAEX_STORE_DATES_TZ }).format(instant);
  } catch (error) {
    const d = instant;
    return (
      d.getFullYear() +
      '-' +
      String(d.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(d.getDate()).padStart(2, '0')
    );
  }
}

// Locale strings + thresholds for the type/date chips: serialized from
// paex.store_drawer.* / section settings by paex-store-chip.liquid; EN
// literals only as the dead-path fallback for hosts without the chip embed.
function paexStoreDrawerStrings() {
  const fromWindow = (typeof window !== 'undefined' && window.paexStoreDrawerText) || {};
  return {
    opensOn: fromWindow.opensOn || 'Opens {date}',
    closesOn: fromWindow.closesOn || 'Closes {date}',
    nowOpen: fromWindow.nowOpen || 'Now open',
    typeSeasonalPopup: fromWindow.typeSeasonalPopup || 'Halloween Store',
    closesDays: fromWindow.closesDays || '{n} days',
    closesDaysOne: fromWindow.closesDaysOne || '1 day',
    closeWindowDays: Number(fromWindow.closeWindowDays) || PAEX_STORE_CLOSING_SOON_DAYS,
  };
}

// Two-state opening + closing line (merchant spec 2026-08-03), all local
// dates (America/Toronto shopper semantics — same clock the hours engine
// uses): opening_date in the FUTURE → "Opens {date}" (coming-soon rows
// STAY selectable — flagged: no design frame rules on this); opening_date
// TODAY OR PAST → "Now open" grand-opening treatment, persists until the
// merchant clears the field (no auto-expiry window). closing_date today
// or later → "Closes {date}", amber urgency inside 14 days. This line is
// date-driven and DISTINCT from the hours-based "Open now" pill: a
// grand-opened store that is closed for the evening keeps its hours line
// ("Opens at 10:00") alongside the green "Now open" date line.
// Two-slot badge state (fresh design 3a + merchant verbal rule): all dates
// evaluate against the warehouse-timezone "today". Slot 2 is FIRST MATCH:
// opening in the future → opens chip · closing inside the urgency window
// (setting, default 14 days) → closes chip with countdown · opening
// today/past → "Now open" (MERCHANT VERBAL RULE, FLAGGED: the fresh design
// uses "New store" with a 30-day recency window instead; no auto-expiry
// here per his earlier ruling) · otherwise nothing. A store past its
// closing date is EXPIRED — dropped from every surface on the date, not on
// the active flag (design 3a).
var PAEX_STORE_CLOSING_SOON_DAYS = 14;
function paexStoreDatesInfo(store, now) {
  const instant = now instanceof Date ? now : new Date();
  const today = paexStoreParseIsoDate(paexStoreDayIso(instant));
  const opening = paexStoreParseIsoDate(store && store.opening);
  const closing = paexStoreParseIsoDate(store && store.closing);
  const info = {
    opensFuture: false,
    nowOpen: false,
    closing: null,
    closingSoon: false,
    daysLeft: null,
    expired: false,
  };
  if (closing && closing.getTime() < today.getTime()) {
    info.expired = true;
    return info;
  }
  if (opening) {
    if (opening.getTime() > today.getTime()) info.opensFuture = true;
    else info.nowOpen = true;
  }
  info.opening = info.opensFuture ? opening : null;
  if (closing) {
    info.closing = closing;
    info.daysLeft = Math.round((closing.getTime() - today.getTime()) / 86400000);
    info.closingSoon = info.daysLeft <= paexStoreDrawerStrings().closeWindowDays;
  }
  return info;
}

function paexStoreExpired(store, now) {
  return paexStoreDatesInfo(store, now).expired;
}

// Design 3a: "On Nov 5 a Halloween popup must be gone without a
// merchandiser touching anything" — prune at ingestion so EVERY surface
// (selector, chip, flyout) drops it on the date.
function paexStorePruneExpired(stores) {
  const pruned = {};
  Object.keys(stores || {}).forEach((id) => {
    if (!paexStoreExpired(stores[id])) pruned[id] = stores[id];
  });
  return pruned;
}

// Chips builder for the drawer rows AND the chip flyout (fresh design 3a/3c:
// pill family — 28px tall, radius 9999, 12px/600, hairline border; type pill
// carries the 🎃 glyph, the date pill never repeats it). classPrefix scopes
// the family per surface ('paex-store-drawer__' | 'paex-store-chip__').
// opts.alwaysClosing: the chip DROPDOWN carries the closing date even
// outside the urgency window ("a saved popup store must not look permanent
// for three months"); rows show it only inside the window. Countdown text
// (" · 13 days") only inside the window. Returns true when anything
// rendered. No innerHTML sinks.
function paexStoreFillChips(el, store, classPrefix, opts) {
  const strings = paexStoreDrawerStrings();
  const dinfo = paexStoreDatesInfo(store);
  const base = classPrefix + 'pillbadge';
  el.textContent = '';
  if (store && store.type === 'seasonal_popup') {
    const type = document.createElement('span');
    type.className = base + ' ' + base + '--type';
    const glyph = document.createElement('span');
    glyph.setAttribute('aria-hidden', 'true');
    glyph.textContent = '🎃';
    type.appendChild(glyph);
    type.appendChild(document.createTextNode(strings.typeSeasonalPopup));
    el.appendChild(type);
  }
  let date = null;
  if (dinfo.opensFuture) {
    date = document.createElement('span');
    date.className = base + ' ' + base + '--opens';
    date.textContent = paexDeliveryFormat(strings.opensOn, {
      date: paexStoreFormatDate(store.opening),
    });
  } else if (dinfo.closing && (dinfo.closingSoon || (opts && opts.alwaysClosing))) {
    date = document.createElement('span');
    date.className = base + ' ' + base + '--closes';
    let text = paexDeliveryFormat(strings.closesOn, { date: paexStoreFormatDate(store.closing) });
    if (dinfo.closingSoon) {
      text +=
        ' · ' +
        (dinfo.daysLeft === 1
          ? strings.closesDaysOne
          : paexDeliveryFormat(strings.closesDays, { n: dinfo.daysLeft }));
    }
    date.textContent = text;
  } else if (dinfo.nowOpen) {
    date = document.createElement('b');
    date.className = base + ' ' + base + '--now ' + classPrefix + 'nowopen';
    date.textContent = strings.nowOpen;
  }
  if (date) el.appendChild(date);
  return Boolean(el.firstChild);
}

function paexDeliveryFormat(template, values) {
  return String(template == null ? '' : template).replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
  );
}

// Staff-data URL guard (defense in depth): the store_page metaobject field
// should be a url type, but the theme can't enforce the definition — only
// same-site paths and http(s) URLs may reach an href.
function paexSafeUrl(value) {
  var url = String(value || '').trim();
  // single leading slash only — "//host" is protocol-relative, i.e. off-site
  return /^(https?:\/\/|\/(?!\/))/i.test(url) ? url : '';
}

// Fill an element from a "{token}" template, wrapping chosen tokens in <b>
// with a class — the design's inline accents (green countdown, "Open now",
// recovery tier) without any innerHTML sink. values: {token: string},
// boldTokens: {token: className}.
function paexFillTemplate(el, template, values, boldTokens) {
  el.textContent = '';
  var parts = String(template == null ? '' : template).split(/(\{\w+\})/);
  parts.forEach(function (part) {
    var match = /^\{(\w+)\}$/.exec(part);
    if (!match) {
      if (part) el.appendChild(document.createTextNode(part));
      return;
    }
    var key = match[1];
    var value = Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : part;
    if (boldTokens && boldTokens[key]) {
      var bold = document.createElement('b');
      bold.className = boldTokens[key];
      bold.textContent = value;
      el.appendChild(bold);
    } else {
      el.appendChild(document.createTextNode(value));
    }
  });
}

var PAEX_CHECK_SVG = '<svg width="9" height="9" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M28.4489 6.29284C29.067 6.76822 29.1826 7.6546 28.7071 8.27259L14.5895 26.6255C14.3419 26.9474 13.9675 27.1472 13.562 27.1734C13.1566 27.1998 12.7595 27.0502 12.4723 26.763L5.41349 19.7042C4.86217 19.1528 4.86217 18.259 5.41349 17.7076C5.96482 17.1563 6.85869 17.1563 7.41003 17.7076L13.3319 23.6295L26.4692 6.55107C26.9445 5.93306 27.831 5.81745 28.4489 6.29284Z" fill="currentColor"/></svg>';
var PAEX_PIN_SVG = '<svg width="14" height="14" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path d="M22.2477 15.6249L25.9431 23.2163C26.0934 23.525 25.9354 23.7776 25.5921 23.7776H2.40795C2.06457 23.7776 1.90657 23.525 2.05689 23.2163L5.75234 15.6249C5.81112 15.5041 5.96912 15.4053 6.1034 15.4053H9.27207C9.36971 15.4053 9.5016 15.4665 9.56552 15.5403C9.7805 15.7887 9.99948 16.0314 10.2178 16.2717C10.4252 16.4997 10.6337 16.7307 10.8405 16.9672H7.08007C6.94575 16.9672 6.78775 17.0659 6.72901 17.1867L4.28093 22.2158H23.719L21.2711 17.1867C21.2123 17.0659 21.0543 16.9672 20.92 16.9672H17.1511C17.3578 16.7307 17.5664 16.4997 17.7738 16.2717C17.9928 16.0307 18.213 15.7885 18.4285 15.54C18.4923 15.4663 18.6239 15.4053 18.7214 15.4053H21.8967C22.031 15.4053 22.1889 15.5041 22.2477 15.6249ZM19.433 10.437C19.433 14.585 15.9798 15.363 14.3557 19.3493C14.2237 19.6732 13.7677 19.6716 13.6357 19.3477C12.1706 15.7536 9.2185 14.768 8.65367 11.5661C8.09756 8.41398 10.28 5.32629 13.4666 5.02508C16.7074 4.71872 19.433 7.25955 19.433 10.437ZM16.8672 10.437C16.8672 8.85124 15.5818 7.56575 13.996 7.56575C12.4103 7.56575 11.1248 8.85124 11.1248 10.437C11.1248 12.0227 12.4103 13.3082 13.996 13.3082C15.5818 13.3082 16.8672 12.0227 16.8672 10.437Z" fill="currentColor"/></svg>';
var PAEX_CLOCK_SVG = '<svg width="13" height="13" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path d="M2 7.256C2 8.184 2.248 9.064 2.744 9.896C3.336 8.232 4.264 6.776 5.528 5.528C6.792 4.28 8.248 3.352 9.896 2.744C9.048 2.248 8.168 2 7.256 2C5.8 2 4.56 2.52 3.536 3.56C2.512 4.6 2 5.832 2 7.256ZM3.512 14C3.512 15.392 3.776 16.72 4.304 17.984C4.832 19.248 5.568 20.368 6.512 21.344V24.512C6.512 24.928 6.656 25.28 6.944 25.568C7.232 25.856 7.584 26 8 26C8.416 26 8.768 25.856 9.056 25.568C9.344 25.28 9.496 24.928 9.512 24.512V23.456C10.968 24.16 12.464 24.512 14 24.512C15.536 24.512 17.04 24.16 18.512 23.456V24.512C18.512 24.928 18.656 25.28 18.944 25.568C19.232 25.856 19.584 26 20 26C20.416 26 20.768 25.856 21.056 25.568C21.344 25.28 21.496 24.928 21.512 24.512V21.344C22.456 20.368 23.192 19.256 23.72 18.008C24.248 16.76 24.512 15.424 24.512 14C24.512 12.576 24.232 11.216 23.672 9.92C23.112 8.624 22.368 7.504 21.44 6.56C20.512 5.616 19.392 4.872 18.08 4.328C16.768 3.784 15.408 3.504 14 3.488C12.592 3.472 11.232 3.752 9.92 4.328C8.608 4.904 7.488 5.648 6.56 6.56C5.632 7.472 4.888 8.592 4.328 9.92C3.768 11.248 3.496 12.608 3.512 14ZM6.512 14C6.512 12.976 6.704 12.008 7.088 11.096C7.472 10.184 8.008 9.384 8.696 8.696C9.384 8.008 10.184 7.472 11.096 7.088C12.008 6.704 12.976 6.504 14 6.488C15.36 6.488 16.616 6.824 17.768 7.496C18.92 8.168 19.832 9.08 20.504 10.232C21.176 11.384 21.512 12.64 21.512 14C21.512 15.008 21.312 15.976 20.912 16.904C20.512 17.832 19.976 18.632 19.304 19.304C18.632 19.976 17.832 20.512 16.904 20.912C15.976 21.312 15.008 21.504 14 21.488C12.64 21.488 11.384 21.16 10.232 20.504C9.08 19.848 8.176 18.936 7.52 17.768C6.864 16.6 6.528 15.344 6.512 14ZM12.512 14C12.512 14.416 12.656 14.768 12.944 15.056C13.232 15.344 13.584 15.488 14 15.488H17C17.416 15.488 17.768 15.344 18.056 15.056C18.344 14.768 18.496 14.416 18.512 14C18.528 13.584 18.376 13.232 18.056 12.944C17.736 12.656 17.384 12.504 17 12.488H15.512V11C15.512 10.584 15.36 10.232 15.056 9.944C14.752 9.656 14.4 9.504 14 9.488C13.6 9.472 13.248 9.624 12.944 9.944C12.64 10.264 12.496 10.616 12.512 11V14ZM18.104 2.744C19.768 3.352 21.224 4.28 22.472 5.528C23.72 6.776 24.648 8.232 25.256 9.896C25.752 9.064 26 8.184 26 7.256C26 5.816 25.488 4.584 24.464 3.56C23.44 2.536 22.2 2.016 20.744 2C19.832 2 18.952 2.248 18.104 2.744Z" fill="currentColor"/></svg>';

function paexDeliveryHasStore(stores, storeId) {
  return Object.prototype.hasOwnProperty.call(stores, storeId);
}

// Google Maps directions URL from the store's postal address — no API key needed
function paexDeliveryDirectionsUrl(store) {
  const parts = [store.address1, store.city, store.province, store.zip].filter(Boolean);
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(parts.join(', '))}`;
}

function paexDeliveryDistanceKm(lat1, lng1, lat2, lng2) {
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLng = (lng2 - lng1) * rad;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Geo data comes from snippets/paex-geotargetly.liquid: window.paexGeo,
// sessionStorage 'paex:geo', and the 'paex:geo-ready' document event.
// Usable geo has finite coordinates OR a region code — region-only results
// (e.g. proxies with no precise fix) must still drive the footprint gate.
function paexDeliveryUsableGeo(geo) {
  if (!geo) return false;
  if (Number.isFinite(geo.lat) && Number.isFinite(geo.lng)) return true;
  return Boolean(geo.regionCode);
}

function paexDeliveryReadGeo() {
  if (paexDeliveryUsableGeo(window.paexGeo)) return window.paexGeo;
  try {
    const raw = sessionStorage.getItem(PAEX_GEO_SESSION_KEY);
    if (!raw) return null;
    const geo = JSON.parse(raw);
    if (paexDeliveryUsableGeo(geo)) return geo;
  } catch (error) {
    // unreadable cache — treat as no geo
  }
  return null;
}

function paexDeliveryWriteGeoCache(coords) {
  try {
    sessionStorage.setItem(PAEX_GEO_SESSION_KEY, JSON.stringify(coords));
  } catch (error) {
    // storage unavailable — geo stays per-load
  }
}

/* ---------- Store Availability v2 shared additions (2026-07-26) ---------- */

var PAEX_GATE_SESSION_KEY = 'paex:gate';

// Typed destination (postal/city from the selector) — beats geo for delivery
// dates and the "Deliver to" line. { region, place, lat, lng }
var paexDestMemory = null; // per-load mirror for storage-less browsers

function paexDestRead() {
  try {
    const raw = sessionStorage.getItem(PAEX_DEST_SESSION_KEY);
    if (raw) {
      const dest = JSON.parse(raw);
      if (dest && dest.region) return dest;
    }
  } catch (error) {
    // unreadable storage — fall through to the mirror
  }
  return paexDestMemory && paexDestMemory.region ? paexDestMemory : null;
}

function paexDestWrite(dest) {
  paexDestMemory = dest;
  try {
    sessionStorage.setItem(PAEX_DEST_SESSION_KEY, JSON.stringify(dest));
  } catch (error) {
    // storage unavailable — the mirror carries this load
  }
  document.dispatchEvent(new CustomEvent(PAEX_DEST_EVENT, { detail: dest }));
}

/* ---------- Postal-code safety net (Sasha 2026-10-12) ---------- */
// IP geo is the gate's only input and on iPhone Safari it is often wrong by
// 100+ km (Private Relay / "Hide IP address") or blocked: a shopper 4 km from
// Halloween Expert Trois-Rivières got the online-only layout with no way back.
// A typed destination (PDP "Deliver to", drawer search, clock sheet, cart
// estimator, shipping page — all paexDestWrite) that lands inside the radius
// PROMOTES to the store version; outside it changes nothing (promote only).
// Read by paex-delivery-method.js and paex-store-chip.js.
var paexFsaTables = {}; // url -> Promise<{ FSA: [lat, lng] }>

function paexFsaTable(url) {
  if (!url) return Promise.resolve({});
  if (!paexFsaTables[url]) {
    paexFsaTables[url] = fetch(url)
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) { return (data && data.fsa) || {}; })
      .catch(function () { return {}; });
  }
  return paexFsaTables[url];
}

// true when the destination can yield a verdict (coordinates, or a Canadian
// postal/FSA in `place`) — callers defer a demotion while the lookup runs
function paexDestResolvable(dest) {
  if (!dest) return false;
  if (Number.isFinite(dest.lat) && Number.isFinite(dest.lng)) return true;
  return Boolean(paexPostalKeys(dest.place));
}

// dest { region, place, lat?, lng? } → Promise<{ inFootprint, nearestId, nearestKm } | null>
function paexDestFootprint(dest, stores, radiusKm, fsaUrl) {
  if (!dest || !stores) return Promise.resolve(null);
  if (Number.isFinite(dest.lat) && Number.isFinite(dest.lng)) {
    return Promise.resolve(paexFootprint({ lat: dest.lat, lng: dest.lng }, stores, radiusKm));
  }
  var keys = paexPostalKeys(dest.place);
  if (!keys) return Promise.resolve(null);
  return paexFsaTable(fsaUrl).then(function (table) {
    for (var i = 0; i < keys.length; i += 1) {
      var hit = table[keys[i]];
      if (hit && isFinite(hit[0]) && isFinite(hit[1])) {
        return paexFootprint({ lat: Number(hit[0]), lng: Number(hit[1]) }, stores, radiusKm);
      }
    }
    return null;
  });
}

/* ---------- Store details page shared additions (2026-07-31) ---------- */

// Tolerant time parser for the hours JSON: whole hours ("10", 10) AND
// half-hours ("10:30") both work everywhere. Returns minutes since midnight,
// or null when the value is missing/unparsable.
function paexStoreParseMinutes(value) {
  if (value == null || value === '') return null;
  var match = /^(\d{1,2})(?::(\d{2}))?$/.exec(String(value).trim());
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + (match[2] ? parseInt(match[2], 10) : 0);
}

// Display label for a minutes value. Whole hours return the NUMBER (exactly
// what the legacy chip/drawer templates rendered from parseInt), non-whole
// return "H:MM" — so "{close}" templates keep working for both.
function paexStoreTimeLabel(minutes) {
  if (minutes == null) return null;
  var hoursPart = Math.floor(minutes / 60);
  var minutesPart = minutes % 60;
  if (minutesPart === 0) return hoursPart;
  return hoursPart + ':' + (minutesPart < 10 ? '0' : '') + minutesPart;
}

// "Now" in an IANA timezone via Intl.DateTimeFormat — DST-safe (never offset
// arithmetic). Returns { weekday: 0(Sun)–6, minutes, dateKey: 'YYYY-MM-DD' }.
var PAEX_TZ_WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
function paexStoreNowParts(now, timeZone) {
  var current = now || new Date();
  try {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone, weekday: 'short', hour: '2-digit', minute: '2-digit',
      hour12: false, year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(current);
    var map = {};
    parts.forEach(function (part) { map[part.type] = part.value; });
    return {
      weekday: PAEX_TZ_WEEKDAYS[map.weekday] != null ? PAEX_TZ_WEEKDAYS[map.weekday] : current.getDay(),
      minutes: (parseInt(map.hour, 10) % 24) * 60 + parseInt(map.minute, 10),
      dateKey: map.year + '-' + map.month + '-' + map.day,
    };
  } catch (error) {
    // unknown timezone / very old engine — browser-local fallback
    return {
      weekday: current.getDay(),
      minutes: current.getHours() * 60 + current.getMinutes(),
      dateKey: null,
    };
  }
}

// First holiday_hours entry matching a YYYY-MM-DD key (past entries never
// match today/tomorrow, so the pool needs no pruning).
function paexStoreHolidayFor(holidays, dateKey) {
  if (!holidays || !holidays.length || !dateKey) return null;
  for (var i = 0; i < holidays.length; i += 1) {
    if (String(holidays[i].date || '').slice(0, 10) === dateKey) return holidays[i];
  }
  return null;
}

// Special-hours window of a holiday_hours entry (status "special_hours"; the
// merchant types "9:00-19:00" / "10h-21h" / "10-21" in the Special hours
// field). Returns {open, close} minutes, or null when absent / unparseable.
// Read by paexStoreHoursInfo for today and tomorrow (Sasha 2026-10-17:
// Halloween late openings must show as "open until 21h" everywhere).
function paexStoreSpecialRange(entry) {
  if (!entry) return null;
  var raw = entry.special != null ? entry.special : entry.special_hours;
  if (raw == null || raw === '' || entry.status === 'closed') return null;
  var m = /^\s*(\d{1,2}(?::\d{2})?)\s*h?\s*(?:-|\u2013|\u00e0|to)\s*(\d{1,2}(?::\d{2})?)\s*h?\s*$/i.exec(String(raw));
  if (!m) return null;
  var open = paexStoreParseMinutes(m[1]);
  var close = paexStoreParseMinutes(m[2]);
  if (open == null || close == null || close <= open) return null;
  return { open: open, close: close };
}

// Open/closed for a store's hours week ({"0"(Sun)…"6": {open,close}}).
// Pure computation — consumers own the wording.
// Without `opts` the behavior is UNCHANGED from v2: browser-local time, no
// holiday awareness (the chip/drawer callers pass nothing and must behave
// identically). opts (all optional): { timeZone: 'America/Toronto',
// holidays: [{date,status,name,special_hours}] }.
// Returns { state: 'open'|'closes_soon'|'closed', closeHour, nextOpenHour,
//           nextOpenIsToday, nextOpenIsTomorrow, nextOpenWeekday } plus,
// when opts.holidays is given, { holidayToday, holidayTomorrow }.
function paexStoreHoursInfo(store, now, opts) {
  var hours = store && store.hours;
  if (!hours) return null;
  var current = now || new Date();
  // Holiday pool: explicit opts.holidays, else the store's own `holidays`
  // (the chip / PDP store blobs carry it since 2026-10-17) — additive: callers
  // that pass nothing and stores without entries behave exactly as before.
  var pool = (opts && opts.holidays) || (store && store.holidays) || null;
  var weekday;
  var minutes;
  var todayKey = null;
  var tomorrowKey = null;
  if (opts && opts.timeZone) {
    var parts = paexStoreNowParts(current, opts.timeZone);
    weekday = parts.weekday;
    minutes = parts.minutes;
    todayKey = parts.dateKey;
    if (todayKey) {
      tomorrowKey = paexStoreNowParts(new Date(current.getTime() + 86400000), opts.timeZone).dateKey;
    }
  } else {
    weekday = current.getDay();
    minutes = current.getHours() * 60 + current.getMinutes();
    if (pool) {
      var pad = function (n) { return (n < 10 ? '0' : '') + n; };
      todayKey = current.getFullYear() + '-' + pad(current.getMonth() + 1) + '-' + pad(current.getDate());
      var tomorrow = new Date(current.getTime() + 86400000);
      tomorrowKey = tomorrow.getFullYear() + '-' + pad(tomorrow.getMonth() + 1) + '-' + pad(tomorrow.getDate());
    }
  }

  var holidayToday = pool ? paexStoreHolidayFor(pool, todayKey) : null;
  var holidayTomorrow = pool ? paexStoreHolidayFor(pool, tomorrowKey) : null;
  var tomorrowClosed = Boolean(holidayTomorrow && holidayTomorrow.status === 'closed');

  var result = { state: 'closed', closeHour: null, nextOpenHour: null,
    nextOpenIsToday: false, nextOpenIsTomorrow: false, nextOpenWeekday: null };
  if (pool) {
    result.holidayToday = holidayToday;
    result.holidayTomorrow = holidayTomorrow;
  }

  var today = hours[String(weekday)];
  var openMin = today ? paexStoreParseMinutes(today.open) : null;
  var closeMin = today ? paexStoreParseMinutes(today.close) : null;
  var specialToday = paexStoreSpecialRange(holidayToday);
  if (specialToday) {
    openMin = specialToday.open;
    closeMin = specialToday.close;
    result.specialToday = true;
  }
  var closedAllDay = Boolean(holidayToday && holidayToday.status === 'closed');

  if (!closedAllDay && openMin != null && closeMin != null && minutes >= openMin && minutes < closeMin) {
    result.state = closeMin - minutes <= 60 ? 'closes_soon' : 'open';
    result.closeHour = paexStoreTimeLabel(closeMin);
    return result;
  }

  // Closed now: opens later today, or roll forward to the next day with hours
  // (skipping holiday-closed days when the caller supplied the pool).
  if (!closedAllDay && openMin != null && closeMin != null && minutes < openMin) {
    result.nextOpenHour = paexStoreTimeLabel(openMin);
    result.nextOpenIsToday = true;
    result.nextOpenWeekday = weekday;
    return result;
  }
  for (var offset = 1; offset <= 7; offset += 1) {
    if (offset === 1 && tomorrowClosed) continue;
    var day = (weekday + offset) % 7;
    var slot = hours[String(day)];
    var slotOpen = slot ? paexStoreParseMinutes(slot.open) : null;
    var slotClose = slot ? paexStoreParseMinutes(slot.close) : null;
    var specialNext = offset === 1 ? paexStoreSpecialRange(holidayTomorrow) : null;
    if (specialNext) { slotOpen = specialNext.open; slotClose = specialNext.close; }
    if (slotOpen != null && slotClose != null) {
      result.nextOpenHour = paexStoreTimeLabel(slotOpen);
      result.nextOpenIsTomorrow = offset === 1;
      result.nextOpenWeekday = day;
      return result;
    }
  }
  return result;
}

// BOPIS polish (plan 6b-3): EN hour labels switch to 12-hour ("9 p.m.") through
// the shared formatter window.paexFulfil.fmtTime (paex-fulfil.js) — the same
// rule as the header chip's hourLabel (paex-store-chip.js) and the PDP block's
// (paex-delivery-method.js), so the drawer rows can never disagree with them.
// Applied ONLY when the template carries the legacy "{close}:00" / "{open}:00"
// suffix (the EN settings); FR templates ("{close} h", Translate & Adapt), a
// page without paexFulfil, or a "Translation missing" formatter answer render
// byte-for-byte as today. value = paexStoreTimeLabel (9 | "9:30").
function paexStoreHourLabel(template, key, value) {
  var api = typeof window !== 'undefined' ? window.paexFulfil : null;
  var tpl = String(template == null ? '' : template);
  var token = '{' + key + '}:00';
  var values = {};
  if (api && typeof api.fmtTime === 'function' && value != null && tpl.indexOf(token) !== -1) {
    var parts = String(value).split(':');
    var hour = parseInt(parts[0], 10);
    var minute = parseInt(parts[1] || '0', 10) || 0;
    var label = '';
    if (isFinite(hour)) {
      try { label = api.fmtTime(hour, minute); } catch (error) { label = ''; }
    }
    if (label && String(label).indexOf('Translation missing') === -1) {
      values[key] = label;
      return paexDeliveryFormat(tpl.split(token).join('{' + key + '}'), values);
    }
  }
  values[key] = value;
  return paexDeliveryFormat(tpl, values);
}

// Compact hours wording shared by chip / block / selector — ONE calculation
// so the surfaces can never disagree. texts keys: openNow, closesAt,
// closesSoon, opensAt, opensTomorrow (all optional; missing = '').
// Optional third arg forwards {timeZone, holidays} to paexStoreHoursInfo;
// legacy two-arg callers behave exactly as before. Hour tokens go through
// paexStoreHourLabel (12-hour EN when the template carries "{close}:00").
function paexStoreHoursText(store, texts, opts) {
  var info = paexStoreHoursInfo(store, null, opts);
  if (!info || !texts) return '';
  if (info.state === 'open' && texts.openNow && texts.closesAt) {
    return texts.openNow + ' · ' + paexStoreHourLabel(texts.closesAt, 'close', info.closeHour);
  }
  if (info.state === 'closes_soon' && texts.closesSoon) {
    return paexStoreHourLabel(texts.closesSoon, 'close', info.closeHour);
  }
  if (info.nextOpenHour != null) {
    if (info.nextOpenIsTomorrow && texts.opensTomorrow) {
      return paexStoreHourLabel(texts.opensTomorrow, 'open', info.nextOpenHour);
    }
    if (texts.opensAt) return paexStoreHourLabel(texts.opensAt, 'open', info.nextOpenHour);
  }
  return '';
}

// Distance-footprint gate (Sasha 2026-07-26 — REPLACES the province gate,
// FAIL CLOSED). With coordinates: in footprint = nearest active store within
// radiusKm. Region-only geo (proxy/no fix): a region that matches at least one
// store's province counts as in-footprint (coarse but better than blind);
// the FSA-centroid asset upgrades typed postal codes to real distance.
// No usable geo at all → OUT (the ecommerce block is right for most of Canada).
// Returns { inFootprint, nearestId, nearestKm }
function paexFootprint(geo, stores, radiusKm) {
  var radius = Number(radiusKm) > 0 ? Number(radiusKm) : 75;
  var result = { inFootprint: false, nearestId: null, nearestKm: null };
  if (!geo) return result;

  if (Number.isFinite(geo.lat) && Number.isFinite(geo.lng)) {
    Object.keys(stores).forEach(function (storeId) {
      var store = stores[storeId];
      if (!Number.isFinite(store.lat) || !Number.isFinite(store.lng)) return;
      var km = paexDeliveryDistanceKm(geo.lat, geo.lng, store.lat, store.lng);
      if (result.nearestKm == null || km < result.nearestKm) {
        result.nearestKm = km;
        result.nearestId = storeId;
      }
    });
    result.inFootprint = result.nearestKm != null && result.nearestKm <= radius;
    return result;
  }

  var region = String(geo.regionCode || '').trim().toUpperCase();
  if (region) {
    result.inFootprint = Object.keys(stores).some(function (storeId) {
      return String(stores[storeId].province || '').trim().toUpperCase() === region;
    });
  }
  return result;
}

// The gate verdict is cached across visits (localStorage, read back for 30
// days by the pre-paint readers of the block and the header chip, so only a
// first-ever visit paints fail-closed and then flips: PDP restyle CLS fix
// 2026-10-05) and for the visit (sessionStorage: the per-session analytics
// tag in paex-geotargetly.liquid and the storage-less fallback). Every page
// still resolves geo in the background and rewrites a stale verdict.
// Chosen-store pages rewrite it to in-footprint.
function paexGateWrite(inFootprint) {
  var value = JSON.stringify({ inFootprint: Boolean(inFootprint), t: Date.now() });
  try {
    localStorage.setItem(PAEX_GATE_SESSION_KEY, value);
  } catch (error) {
    // storage unavailable — the session entry below still serves this visit
  }
  try {
    sessionStorage.setItem(PAEX_GATE_SESSION_KEY, value);
  } catch (error) {
    // storage unavailable — verdict recomputes per page
  }
}

// Search-term folding for the city autocomplete: lowercase, strip diacritics
// (Quebec→quebec matches Québec), fold hyphens/periods to spaces, expand the
// St/Ste prefixes to Saint/Sainte — applied to BOTH sides of the comparison.
function paexFoldSearch(value) {
  var folded = String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[-.']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  folded = folded.replace(/(^|\s)st(\s)/g, '$1saint$2').replace(/(^|\s)ste(\s)/g, '$1sainte$2');
  return folded;
}

// First postal letter -> province (Canada Post FSA scheme) — drives the
// transit table and the free-shipping exclusion check for typed postals.
var PAEX_POSTAL_PROVINCE = { A:'NL', B:'NS', C:'PE', E:'NB', G:'QC', H:'QC',
  J:'QC', K:'ON', L:'ON', M:'ON', N:'ON', P:'ON', R:'MB', S:'SK', T:'AB',
  V:'BC', X:'NT', Y:'YT' };

function paexProvinceFromPostal(value) {
  var letter = String(value || '').trim().charAt(0).toUpperCase();
  return PAEX_POSTAL_PROVINCE[letter] || null;
}

// Shipping zones by postal prefix (Free shipping offer - PE, 2026-10-19).
// Rules = settings.paex_shipping_zone_rules, one "PREFIX|zone" per line,
// seeded from the Checkout Blocks "Hide delivery methods" lists: Remote list →
// remote, Regional list → regional, everything else (* fallback) → metro =
// Canada Standard. Longest matching prefix wins (J0M1E regional inside the
// J0M1x remote block); spaces are stripped on both sides, so "J0M 1A*" in the
// admin and "J0M 1A0" typed by a shopper meet. Returns null when nothing
// matches and no * fallback exists. Shared by the shipping page checker
// (assets/paex-shipping.js) and the offer engine (assets/paex-shipping-offer.js).
function paexParseZones(text) {
  var rules = [];
  String(text || '').split(/\r?\n/).forEach(function (line) {
    var parts = line.split('|');
    if (parts.length !== 2) return;
    var prefix = parts[0].replace(/\s+/g, '').toUpperCase();
    var zone = parts[1].trim().toLowerCase();
    if (!prefix || !/^(metro|regional|remote)$/.test(zone)) return;
    rules.push([prefix, zone]);
  });
  return rules;
}

function paexZoneFor(postal, rules) {
  var raw = String(postal || '').replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]\d/.test(raw)) return null;
  var best = null;
  var fallback = null;
  (rules || []).forEach(function (rule) {
    if (rule[0] === '*') { fallback = rule[1]; return; }
    if (raw.indexOf(rule[0]) === 0 && (!best || rule[0].length > best[0].length)) best = rule;
  });
  if (best) return best[1];
  return fallback || null;
}

// Canadian postal pattern: full (H7P 4W7), FSA-only (H7P), or a letter-digit
// prefix (H7). Returns the normalized candidate keys, most specific first.
function paexPostalKeys(value) {
  var raw = String(value || '').toUpperCase().replace(/\s+/g, '');
  if (!/^[A-Z]\d/.test(raw)) return null;
  if (!/^[A-Z]\d(?:[A-Z](?:\d(?:[A-Z]\d?)?)?)?$/.test(raw)) return null;
  var keys = [];
  if (raw.length >= 3) keys.push(raw.slice(0, 3));
  keys.push(raw.slice(0, 2));
  return keys;
}

if (!customElements.get('paex-store-drawer')) {
  customElements.define(
    'paex-store-drawer',
    class PaexStoreDrawer extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        // any drive-to-store host exposing data.{stores,texts}, storeId,
        // variantId (nullable) and a stock provider can own a drawer. The
        // header chip's drawer is a SIBLING (an ancestor drawer would be
        // hidden with the desktop chip on mobile), hence the document fallback.
        this.host =
          this.closest('paex-delivery-method, paex-store-chip') ||
          document.querySelector('paex-store-chip');
        this.modal = this.closest('modal-dialog');
        this.origin = null; // {lat,lng,label} — geolocation or typed city/postal
        this.hasItemOnly = false;
        this.openNowOnly = false;
        this.geoData = null; // lazy: {cities:[…], fsa:{…}}
        this.suggestIndex = -1;

        const geo = paexDeliveryReadGeo();
        if (geo && Number.isFinite(geo.lat) && Number.isFinite(geo.lng)) {
          this.origin = { lat: geo.lat, lng: geo.lng, label: '' };
        }

        // Built eagerly: Ascent's focus trap snapshots focusables synchronously
        // on show(), so the rows must exist before the drawer ever opens.
        if (this.host && this.host.data) {
          this.buildList();
        } else if (this.host) {
          customElements.whenDefined(this.host.localName).then(() => {
            if (!this.abortController.signal.aborted) this.buildList();
          });
        }

        if (this.modal) {
          this.openObserver = new MutationObserver(() => {
            if (this.modal.hasAttribute('open')) {
              // late-arriving geo: first-visit sessions often resolve geo
              // AFTER connect — pick it up so distances/sort work on open
              if (!this.origin) {
                const lateGeo = paexDeliveryReadGeo();
                if (lateGeo && Number.isFinite(lateGeo.lat) && Number.isFinite(lateGeo.lng)) {
                  this.origin = { lat: lateGeo.lat, lng: lateGeo.lng, label: '' };
                  this.applyDistances();
                }
              }
              this.loadGeoData();
              this.refreshRows();
            }
          });
          this.openObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
        }

        this.bindTools();
      }

      disconnectedCallback() {
        this.abortController.abort();
        if (this.openObserver) this.openObserver.disconnect();
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
      }

      bindTools() {
        const signal = this.abortController.signal;
        const filter = this.querySelector('[data-store-filter]');
        if (filter) {
          filter.addEventListener('input', () => {
            if (this.debounceTimer) clearTimeout(this.debounceTimer);
            // ~250ms debounce per the design's autocomplete spec
            this.debounceTimer = setTimeout(() => this.onSearchInput(filter.value), 250);
          }, { signal });
          filter.addEventListener('keydown', (event) => this.onSearchKeydown(event), { signal });
          // Ascent's ModalDialog closes on Escape KEYUP — while the suggestion
          // list is open, the first Escape must close only the list.
          filter.addEventListener('keyup', (event) => {
            if (event.key !== 'Escape') return;
            const listbox = this.querySelector('[data-store-suggest-list]');
            if (listbox && !listbox.hidden) {
              event.stopPropagation();
              this.renderSuggestions([]);
            }
          }, { signal });
          filter.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
              const listbox = this.querySelector('[data-store-suggest-list]');
              if (listbox && !listbox.hidden) event.stopPropagation();
            }
          }, { signal });
        }

        const locate = this.querySelector('[data-use-location]');
        if (locate) {
          locate.addEventListener('click', () => this.useMyLocation(), { signal });
        }

        const hasItem = this.querySelector('[data-filter-has-item]');
        if (hasItem) {
          hasItem.addEventListener('click', () => {
            this.hasItemOnly = !this.hasItemOnly;
            hasItem.setAttribute('aria-pressed', String(this.hasItemOnly));
            hasItem.classList.toggle('is-active', this.hasItemOnly);
            this.applyFilters();
          }, { signal });
          // visibility decided per refresh — the host attaches after bind
        }

        const openNow = this.querySelector('[data-filter-open-now]');
        if (openNow) {
          openNow.addEventListener('click', () => {
            this.openNowOnly = !this.openNowOnly;
            openNow.setAttribute('aria-pressed', String(this.openNowOnly));
            openNow.classList.toggle('is-active', this.openNowOnly);
            this.applyFilters();
          }, { signal });
        }
      }

      // The city/FSA datasets load once, on the drawer's FIRST open — never on
      // page load (performance budget: the JSON costs nothing until needed).
      loadGeoData() {
        if (this.geoData || this.geoLoading) return;
        const citiesUrl = this.dataset.citiesUrl;
        const fsaUrl = this.dataset.fsaUrl;
        if (!citiesUrl || !fsaUrl) return;
        this.geoLoading = true;
        Promise.all([
          fetch(citiesUrl).then((response) => (response.ok ? response.json() : null)),
          fetch(fsaUrl).then((response) => (response.ok ? response.json() : null)),
        ])
          .then(([cities, fsa]) => {
            this.geoData = {
              cities: (cities && cities.cities) || [],
              fsa: (fsa && fsa.fsa) || {},
            };
            this.geoData.cities.forEach((city) => {
              city.folded = paexFoldSearch(city.n);
            });
          })
          .catch(() => {
            this.geoData = { cities: [], fsa: {} };
          });
      }

      setStatus(message) {
        const status = this.querySelector('[data-store-status]');
        if (status && status.textContent !== message) status.textContent = message;
      }

      texts() {
        return (this.host && this.host.data && this.host.data.texts) || {};
      }

      stockTextFor(storeId) {
        if (!this.host || !this.host.variantId) return '';
        return this.host.tierTextFor(this.host.variantId, storeId);
      }

      stockStateFor(storeId) {
        if (!this.host || !this.host.variantId) return '';
        return this.host.tierStateFor(this.host.variantId, storeId);
      }

      /* ---------------- rows (design 1d anatomy) ---------------- */

      buildList() {
        if (!this.host || !this.host.data) return;
        const list = this.querySelector('[data-store-list]');
        if (!list) return;
        list.textContent = ''; // editor detach/reattach must not duplicate rows
        const texts = this.texts();

        const fragment = document.createDocumentFragment();
        Object.keys(this.host.data.stores).forEach((storeId) => {
          const store = this.host.data.stores[storeId];
          const item = document.createElement('li');
          item.className = 'paex-store-drawer__item';
          item.dataset.storeId = storeId;
          item.dataset.city = paexFoldSearch(store.city);

          const dot = document.createElement('span');
          dot.className = 'paex-store-drawer__dot';
          dot.dataset.storeDot = '';
          dot.setAttribute('aria-hidden', 'true');
          item.appendChild(dot);

          const main = document.createElement('div');
          main.className = 'paex-store-drawer__main';

          const nameRow = document.createElement('div');
          nameRow.className = 'paex-store-drawer__namerow';
          const name = document.createElement('span');
          name.className = 'paex-store-drawer__name';
          // 1c sheet shows SHORT names; 1d desktop keeps the full banner name
          name.textContent = window.matchMedia && window.matchMedia('(max-width: 749px)').matches
            ? paexDeliveryShortName(store.name)
            : store.name || '';
          nameRow.appendChild(name);
          if (texts.selYourStore) {
            const badge = document.createElement('span');
            badge.className = 'paex-store-drawer__badge';
            badge.dataset.yourStore = '';
            badge.innerHTML = PAEX_CHECK_SVG; // static constant, no user data
            badge.appendChild(document.createTextNode(texts.selYourStore));
            badge.hidden = true;
            nameRow.appendChild(badge);
          }
          const distance = document.createElement('span');
          distance.className = 'paex-store-drawer__distance';
          distance.dataset.storeDistance = '';
          nameRow.appendChild(distance);
          main.appendChild(nameRow);

          // type/date chips (fresh design 3c: under the store name, above
          // the address — after the identity, before the logistics); the
          // container exists only for stores that can ever carry a chip
          if (store.type === 'seasonal_popup' || store.opening || store.closing) {
            const chips = document.createElement('span');
            chips.className = 'paex-store-drawer__chips';
            chips.dataset.storeChips = '';
            chips.hidden = true; // filled by refreshRows
            main.appendChild(chips);
          }

          const qty = document.createElement('span');
          qty.className = 'paex-store-drawer__qty';
          qty.dataset.storeQty = '';
          main.appendChild(qty);

          const metaLine = document.createElement('span');
          metaLine.className = 'paex-store-drawer__metaline';
          const meta = document.createElement('span');
          meta.className = 'paex-store-drawer__address';
          meta.dataset.storeMeta = '';
          meta.textContent = [store.address1, store.city].filter(Boolean).join(', ');
          metaLine.appendChild(meta);
          const hours = document.createElement('span');
          hours.className = 'paex-store-drawer__hours';
          hours.dataset.storeHours = '';
          metaLine.appendChild(hours);
          main.appendChild(metaLine);


          // Actions: Set as my store / Directions / Store details — always the
          // same three; the selected store swaps Set for the badge + phone.
          const actions = document.createElement('span');
          actions.className = 'paex-store-drawer__links';

          const setButton = document.createElement('button');
          setButton.type = 'button';
          setButton.className = 'paex-store-drawer__setbtn';
          setButton.dataset.setStore = '';
          setButton.textContent = texts.selSetStore || texts.select || '';
          setButton.addEventListener(
            'click',
            () => {
              document.dispatchEvent(new CustomEvent(PAEX_STORE_EVENT, { detail: { storeId } }));
              this.refreshRows();
              if (this.modal) this.modal.hide();
            },
            { signal: this.abortController.signal }
          );
          actions.appendChild(setButton);

          const directions = document.createElement('a');
          directions.className = 'link';
          directions.dataset.storeDirections = '';
          directions.href = paexDeliveryDirectionsUrl(store);
          directions.target = '_blank';
          directions.rel = 'noopener';
          directions.textContent = texts.directions || '';
          actions.appendChild(directions);

          if (store.phone) {
            const phone = document.createElement('a');
            phone.className = 'link paex-store-drawer__phone';
            phone.dataset.storePhone = '';
            phone.href = `tel:${String(store.phone).replace(/[^+\d]/g, '')}`;
            phone.textContent = texts.call || store.phone;
            phone.hidden = true; // shown on the selected row only
            actions.appendChild(phone);
          }

          if (paexSafeUrl(store.page) && texts.details) {
            const page = document.createElement('a');
            page.className = 'link';
            page.dataset.storeDetails = '';
            page.href = paexSafeUrl(store.page);
            page.textContent = texts.details;
            actions.appendChild(page);
          }

          main.appendChild(actions);
          item.appendChild(main);
          fragment.appendChild(item);
        });
        list.appendChild(fragment);

        this.refreshRows();
      }

      refreshRows() {
        if (!this.host || !this.host.data) return;
        const texts = this.texts();
        // no variant context (header chip drawer) → the has-item pill is
        // meaningless; PDP hosts attach late, so decide here every refresh
        const hasItemPill = this.querySelector('[data-filter-has-item]');
        if (hasItemPill) hasItemPill.hidden = !this.host.variantId;
        this.querySelectorAll('.paex-store-drawer__item').forEach((item) => {
          const storeId = item.dataset.storeId;
          const store = this.host.data.stores[storeId];

          const qtyTarget = item.querySelector('[data-store-qty]');
          let rowState = '';
          if (qtyTarget) {
            const text = this.stockTextFor(storeId);
            rowState = this.stockStateFor(storeId);
            qtyTarget.dataset.state = rowState;
            item.dataset.state = rowState;
            if (qtyTarget.textContent !== text) qtyTarget.textContent = text;
          }

          const hoursTarget = item.querySelector('[data-store-hours]');
          let hoursShort = '';
          if (hoursTarget) {
            const info = paexStoreHoursInfo(store);
            if (info && info.state === 'open' && texts.openNow && texts.closesAt) {
              paexFillTemplate(hoursTarget, '{open}, {closes}', {
                open: texts.openNow,
                closes: paexStoreHourLabel(texts.closesAt, 'close', info.closeHour),
              }, { open: 'paex-store-drawer__open' });
              hoursShort = paexStoreHourLabel(texts.closesAt, 'close', info.closeHour);
            } else if (info && info.state === 'closes_soon' && texts.closesSoon) {
              // 1d: bold-amber phrase, plain time — split the merchant
              // template at its {close} token (the "{close}:00" tail rides along so the
              // 12-hour label applies)
              const soonParts = String(texts.closesSoon).split('{close}');
              paexFillTemplate(hoursTarget, '{soon}{time}', {
                soon: soonParts[0],
                time: paexStoreHourLabel('{close}' + (soonParts[1] || ''), 'close', info.closeHour),
              }, { soon: 'paex-store-drawer__soon' });
              hoursShort = paexStoreHourLabel(texts.closesAt || '', 'close', info.closeHour);
            } else {
              const text = paexStoreHoursText(store, texts);
              if (hoursTarget.textContent !== text) hoursTarget.textContent = text;
              hoursShort = text;
            }
            item.dataset.open = info && info.state !== 'closed' ? 'true' : 'false';
          }

          // dates line (recomputed each refresh so midnight flips states):
          // "Opens {date}" (future) | green "Now open" (today/past), then
          // " · Closes {date}" (amber when <= 14 days out)
          // type/date chips + the unopened-store rule (fresh design 3a:
          // "No stock count and no Directions on a store that isn't open
          // yet — a count would send someone to a locked door")
          const chipsTarget = item.querySelector('[data-store-chips]');
          const dinfo = paexStoreDatesInfo(store);
          if (chipsTarget) {
            chipsTarget.hidden = !paexStoreFillChips(chipsTarget, store, 'paex-store-drawer__');
          }
          if (dinfo.opensFuture && qtyTarget) {
            qtyTarget.textContent = '';
            qtyTarget.dataset.state = '';
            item.dataset.state = '';
          }
          const directionsForOpen = item.querySelector('[data-store-directions]');
          if (directionsForOpen) directionsForOpen.hidden = dinfo.opensFuture;

          // selected row: YOUR STORE badge + phone, no Set-as-my-store
          const selected = storeId === this.host.storeId;
          item.classList.toggle('is-selected', selected);
          if (selected) item.setAttribute('aria-current', 'true');
          else item.removeAttribute('aria-current');
          const badge = item.querySelector('[data-your-store]');
          if (badge) badge.hidden = !selected;
          const setButton = item.querySelector('[data-set-store]');
          if (setButton) setButton.hidden = selected;
          const phone = item.querySelector('[data-store-phone]');
          if (phone) phone.hidden = !selected;
          const directionsLink = item.querySelector('[data-store-directions]');
          if (directionsLink) {
            directionsLink.classList.toggle('paex-store-drawer__pilllink', selected);
          }
          // 1c/1d: out-of-stock rows collapse to "Out of stock · closes 17:00"
          if (rowState === 'out' && qtyTarget && hoursShort
              && window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
            const compactStatus = `${this.stockTextFor(storeId)} · ${hoursShort}`;
            if (qtyTarget.textContent !== compactStatus) qtyTarget.textContent = compactStatus;
          }
        });
        this.applyDistances();
        this.applyFilters();
      }

      /* ---------------- filters + count line ---------------- */

      applyFilters() {
        if (!this.host || !this.host.data) return;
        const texts = this.texts();
        let visible = 0;
        let withStock = 0;
        this.querySelectorAll('.paex-store-drawer__item').forEach((item) => {
          const stocked = item.dataset.state && item.dataset.state !== 'out';
          if (stocked) withStock += 1;
          let hidden = false;
          if (this.hasItemOnly && !stocked) hidden = true;
          if (this.openNowOnly && item.dataset.open !== 'true') hidden = true;
          item.hidden = hidden;
          if (!hidden) visible += 1;
        });

        const empty = this.querySelector('[data-store-empty]');
        if (empty) {
          empty.hidden = visible !== 0;
          if (!empty.hidden) {
            empty.textContent = paexDeliveryFormat(texts.selNoResults || '', {
              city: (this.origin && this.origin.label) || '',
            });
          }
        }

        const total = Object.keys(this.host.data.stores).length;
        if (this.host.variantId && texts.selCount) {
          this.setStatus(paexDeliveryFormat(texts.selCount, {
            n: withStock,
            total: total,
            origin: (this.origin && this.origin.label) || texts.selYou || '',
          }));
        } else if (texts.selCountAll) {
          this.setStatus(paexDeliveryFormat(texts.selCountAll, { total: total }));
        }

        const allLink = this.querySelector('[data-all-locations]');
        if (allLink) {
          allLink.textContent = paexDeliveryFormat(allLink.dataset.label || '', { n: total });
        }

        this.rearmFocusTrap();
      }

      /* ---------------- city / postal autocomplete ---------------- */

      onSearchInput(rawValue) {
        const value = String(rawValue || '').trim();
        const postal = paexPostalKeys(value);
        if (postal) {
          this.renderSuggestions(this.postalSuggestion(value, postal));
          return;
        }
        // suggestions open at 4 characters per the design
        if (value.length < 4 || !this.geoData) {
          this.renderSuggestions([]);
          return;
        }
        const folded = paexFoldSearch(value);
        const stores = this.host && this.host.data ? this.host.data.stores : {};
        const matches = this.geoData.cities
          .filter((city) => city.folded.startsWith(folded) || city.folded.includes(' ' + folded))
          .map((city) => {
            let count = 0;
            let nearest = null;
            Object.keys(stores).forEach((storeId) => {
              const store = stores[storeId];
              if (paexFoldSearch(store.city) === city.folded) count += 1;
              if (Number.isFinite(store.lat) && Number.isFinite(store.lng)) {
                const km = paexDeliveryDistanceKm(city.lat, city.lng, store.lat, store.lng);
                if (nearest == null || km < nearest) nearest = km;
              }
            });
            return { type: 'city', city: city, count: count, nearest: nearest };
          })
          .sort((a, b) => (b.count - a.count) || ((a.nearest || 1e9) - (b.nearest || 1e9)))
          .slice(0, 6); // max 6 per the design
        this.renderSuggestions(matches);
      }

      postalSuggestion(value, keys) {
        if (!this.geoData) return [];
        for (let i = 0; i < keys.length; i += 1) {
          const hit = this.geoData.fsa[keys[i]];
          if (hit) {
            return [{
              type: 'postal',
              label: value.toUpperCase(),
              lat: hit[0],
              lng: hit[1],
            }];
          }
        }
        return [];
      }

      renderSuggestions(items) {
        const listbox = this.querySelector('[data-store-suggest-list]');
        const input = this.querySelector('[data-store-filter]');
        if (!listbox || !input) return;
        listbox.textContent = '';
        this.suggestIndex = -1;
        this.suggestions = items;
        input.removeAttribute('aria-activedescendant');
        const texts = this.texts();

        if (!items.length) {
          listbox.hidden = true;
          input.setAttribute('aria-expanded', 'false');
          input.removeAttribute('aria-activedescendant');
          this.rearmFocusTrap();
          return;
        }

        const typedInput = this.querySelector('[data-store-filter]');
        const typed = paexFoldSearch(String((typedInput && typedInput.value) || '').trim());
        items.forEach((suggestion, index) => {
          const option = document.createElement('li');
          option.className = 'paex-store-drawer__option';
          option.id = `${listbox.id}-option-${index}`;
          option.setAttribute('role', 'option');
          const pin = document.createElement('span');
          pin.className = 'paex-store-drawer__option-pin';
          pin.setAttribute('aria-hidden', 'true');
          pin.innerHTML = PAEX_PIN_SVG; // static constant, no user data
          option.appendChild(pin);
          const label = document.createElement('span');
          label.className = 'paex-store-drawer__option-label';
          if (suggestion.type === 'postal') {
            label.textContent = suggestion.label;
          } else {
            // typed-prefix highlight (design 1d: the matched chars carry a mark)
            const cityName = suggestion.city.n;
            const folded = paexFoldSearch(cityName);
            const at = typed ? folded.indexOf(typed) : -1;
            if (at === 0 && typed.length > 0 && typed.length <= cityName.length) {
              const mark = document.createElement('mark');
              mark.className = 'paex-store-drawer__match';
              mark.textContent = cityName.slice(0, typed.length);
              label.appendChild(mark);
              label.appendChild(document.createTextNode(cityName.slice(typed.length)));
            } else {
              label.appendChild(document.createTextNode(cityName));
            }
            const prov = document.createElement('span');
            prov.className = 'paex-store-drawer__option-prov';
            prov.textContent = `, ${suggestion.city.p}`;
            label.appendChild(prov);
          }
          option.appendChild(label);
          const side = document.createElement('span');
          side.className = 'paex-store-drawer__option-side';
          if (suggestion.type === 'city') {
            const cityTemplate = suggestion.count === 1 && texts.selCityStoresOne
              ? texts.selCityStoresOne
              : texts.selCityStores;
            side.textContent = suggestion.count > 0
              ? paexDeliveryFormat(cityTemplate || '', { n: suggestion.count })
              : paexDeliveryFormat(texts.selCityNearest || '', {
                  km: suggestion.nearest != null ? Math.round(suggestion.nearest) : '—',
                });
          }
          option.appendChild(side);
          option.addEventListener('mousedown', (event) => {
            event.preventDefault(); // keep focus in the input
            this.pickSuggestion(index);
          }, { signal: this.abortController.signal });
          listbox.appendChild(option);
        });
        listbox.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        this.rearmFocusTrap();
      }

      onSearchKeydown(event) {
        const listbox = this.querySelector('[data-store-suggest-list]');
        if (!listbox || listbox.hidden) return;
        const max = (this.suggestions || []).length - 1;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.suggestIndex = event.key === 'ArrowDown'
            ? Math.min(max, this.suggestIndex + 1)
            : Math.max(0, this.suggestIndex - 1);
          const input = this.querySelector('[data-store-filter]');
          listbox.querySelectorAll('[role="option"]').forEach((option, index) => {
            const active = index === this.suggestIndex;
            option.classList.toggle('is-active', active);
            if (active) option.setAttribute('aria-selected', 'true');
            else option.removeAttribute('aria-selected');
          });
          const active = listbox.children[this.suggestIndex];
          if (active && input) input.setAttribute('aria-activedescendant', active.id);
        } else if (event.key === 'Enter') {
          if (this.suggestIndex >= 0) {
            event.preventDefault();
            this.pickSuggestion(this.suggestIndex);
          } else if ((this.suggestions || []).length === 1) {
            event.preventDefault();
            this.pickSuggestion(0);
          }
        }
      }

      pickSuggestion(index) {
        const suggestion = (this.suggestions || [])[index];
        if (!suggestion) return;
        const input = this.querySelector('[data-store-filter]');
        if (suggestion.type === 'postal') {
          this.origin = { lat: suggestion.lat, lng: suggestion.lng, label: suggestion.label };
          if (input) input.value = suggestion.label;
          // a typed postal is also the delivery destination; the FSA's first
          // letter decides the province (shared Canada Post map)
          const region = paexProvinceFromPostal(suggestion.label) || 'QC';
          paexDestWrite({ region: region, place: suggestion.label, lat: suggestion.lat, lng: suggestion.lng });
        } else {
          this.origin = { lat: suggestion.city.lat, lng: suggestion.city.lng, label: suggestion.city.n };
          if (input) input.value = suggestion.city.n;
          paexDestWrite({ region: suggestion.city.p, place: suggestion.city.n, lat: suggestion.city.lat, lng: suggestion.city.lng });
        }
        this.renderSuggestions([]);
        this.applyDistances();
        this.applyFilters();
      }

      // Ascent's focus trap snapshots first/last focusables once at show(); any
      // change to what's visible/ordered inside the open drawer must re-arm it
      // or Tab can escape the modal from the real last element. trapFocus resets
      // a focused input's caret to the end — preserve the selection around it
      // so typing mid-string in the filter doesn't jump the caret.
      rearmFocusTrap() {
        if (this.modal && this.modal.hasAttribute('open')) {
          const el = document.activeElement;
          const isText = el && el.tagName === 'INPUT' && typeof el.selectionStart === 'number';
          const start = isText ? el.selectionStart : null;
          const end = isText ? el.selectionEnd : null;
          webvista.trapFocus(this.modal, el);
          if (isText) el.setSelectionRange(start, end);
        }
      }

      // The only place a browser geolocation permission prompt can ever fire
      // from the selector — an explicit user tap.
      useMyLocation() {
        if (!navigator.geolocation || !this.host || !this.host.data) return;
        const texts = this.texts();
        this.setStatus(texts.locating || '');
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = { lat: position.coords.latitude, lng: position.coords.longitude };
            this.origin = { lat: coords.lat, lng: coords.lng, label: '' };
            // merge into the cached object so regionCode from the site-wide
            // loader survives, then rebroadcast so the block/chip gates update
            const merged = Object.assign({}, paexDeliveryReadGeo(), coords);
            paexDeliveryWriteGeoCache(merged);
            // source tags a shopper-requested precise fix (event only, never
            // cached): the PDP block re-picks its store from it (round 2 P6)
            document.dispatchEvent(new CustomEvent(PAEX_GEO_EVENT, { detail: Object.assign({ source: 'gps' }, merged) }));
            this.applyDistances();
            this.applyFilters();
            this.setStatus(texts.sorted || '');
          },
          () => {
            this.setStatus(texts.geoFail || '');
          }
        );
      }

      applyDistances() {
        if (!this.host || !this.host.data) return;
        const list = this.querySelector('[data-store-list]');
        if (!list) return;
        const origin = this.origin;

        const items = Array.from(list.querySelectorAll('.paex-store-drawer__item'));
        items.forEach((item) => {
          const store = this.host.data.stores[item.dataset.storeId];
          const target = item.querySelector('[data-store-distance]');
          if (!origin || !store || !target ||
              !Number.isFinite(store.lat) || !Number.isFinite(store.lng)) {
            item.dataset.distance = '';
            if (target) target.textContent = '';
            return;
          }
          const km = paexDeliveryDistanceKm(origin.lat, origin.lng, store.lat, store.lng);
          item.dataset.distance = String(km);
          target.textContent = `${km.toFixed(1)} km`;
        });

        // nearest first (stores without coords sink to the end)
        items
          .slice()
          .sort((a, b) => {
            const da = a.dataset.distance === '' ? Infinity : Number(a.dataset.distance);
            const db = b.dataset.distance === '' ? Infinity : Number(b.dataset.distance);
            return da - db;
          })
          .forEach((item) => list.appendChild(item));

        this.rearmFocusTrap();
      }
    }
  );
}
